/**
 * Indents an XML or JSON document for reading without changing what it says.
 *
 * Only whitespace between structural tokens is rewritten: JSON numbers and strings, XML text,
 * attributes, comments and CDATA are copied from the source character for character. So "1.10"
 * stays "1.10" (JSON.parse + stringify would print 1.1), and an XML element holding text — mixed
 * content, an `xsl:text`, anything under `xml:space="preserve"` — is copied verbatim, because
 * the whitespace inside it can be significant.
 */

export type ContentFormat = 'xml' | 'json';

const INDENT = '  ';

/** The format a stored format name maps to (e.g. a staging record's rawMessageFormat), if any. */
export function toContentFormat(value: unknown): ContentFormat | undefined {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return normalized === 'xml' || normalized === 'json' ? normalized : undefined;
}

/** The indented document, or null when the content does not parse as the given format. */
export function formatContent(content: string, format: ContentFormat): string | null {
  return format === 'xml' ? formatXml(content) : formatJson(content);
}

// ---------------------------------------------------------------- JSON

function formatJson(content: string): string | null {
  try {
    JSON.parse(content);
  } catch {
    return null;
  }
  return reindentJson(content);
}

/** Re-emits the (valid) JSON with one value per line; string literals are copied as they are. */
function reindentJson(content: string): string {
  let out = '';
  let depth = 0;
  let i = 0;
  while (i < content.length) {
    const ch = content[i];
    if (ch === '"') {
      const end = endOfJsonString(content, i);
      out += content.slice(i, end);
      i = end;
      continue;
    }
    out += jsonStructural(ch, depth, content, i);
    depth += ch === '{' || ch === '[' ? 1 : ch === '}' || ch === ']' ? -1 : 0;
    i += 1;
  }
  return out;
}

function jsonStructural(ch: string, depth: number, content: string, i: number): string {
  if (/\s/.test(ch)) return '';
  const newline = (d: number) => '\n' + INDENT.repeat(d);
  if (ch === '{' || ch === '[') {
    return isEmptyJsonContainer(content, i) ? ch : ch + newline(depth + 1);
  }
  if (ch === '}' || ch === ']') {
    return isEmptyJsonContainer(content, previousNonSpace(content, i)) ? ch : newline(depth - 1) + ch;
  }
  if (ch === ',') return ',' + newline(depth);
  if (ch === ':') return ': ';
  return ch;
}

function endOfJsonString(content: string, start: number): number {
  let i = start + 1;
  while (i < content.length && content[i] !== '"') {
    i += content[i] === '\\' ? 2 : 1;
  }
  return i + 1;
}

function previousNonSpace(content: string, i: number): number {
  let j = i - 1;
  while (j >= 0 && /\s/.test(content[j])) j -= 1;
  return j;
}

/** True for the opening bracket of `{}` / `[]` (whitespace inside allowed). */
function isEmptyJsonContainer(content: string, openIndex: number): boolean {
  const open = content[openIndex];
  if (open !== '{' && open !== '[') return false;
  let j = openIndex + 1;
  while (j < content.length && /\s/.test(content[j])) j += 1;
  return content[j] === (open === '{' ? '}' : ']');
}

// ---------------------------------------------------------------- XML

interface XmlNode {
  kind: 'element' | 'text' | 'other';
  /** Source offsets of the whole node — for an element, from its start tag to its end tag. */
  start: number;
  end: number;
  /** The start tag (or the self-closing tag) as written. */
  startTag?: string;
  endTag?: string;
  children: XmlNode[];
}

// Comments, CDATA, processing instructions, a DOCTYPE (with an internal subset), end tags,
// start/empty tags (attribute values may contain '>'), and text.
const XML_TOKEN =
  /<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>|<\?[\s\S]*?\?>|<!DOCTYPE(?:[^[>]|\[[^\]]*\])*>|<\/[^>]+>|<(?:[^>"']|"[^"]*"|'[^']*')+>|[^<]+/g;

function formatXml(content: string): string | null {
  if (!isWellFormedXml(content)) return null;
  const nodes = parseXmlNodes(content);
  if (!nodes) return null;
  return nodes
    .filter((node) => !isWhitespaceText(node, content))
    .map((node) => emitXmlNode(node, content, 0))
    .join('\n');
}

function isWellFormedXml(content: string): boolean {
  const doc = new DOMParser().parseFromString(content, 'application/xml');
  return doc.getElementsByTagName('parsererror').length === 0;
}

/** A light tree over the source offsets; null if the tags do not balance. */
function parseXmlNodes(content: string): XmlNode[] | null {
  const root: XmlNode = { kind: 'element', start: 0, end: content.length, children: [] };
  const stack: XmlNode[] = [root];
  for (const match of content.matchAll(XML_TOKEN)) {
    const token = match[0];
    const start = match.index ?? 0;
    const parent = stack[stack.length - 1];
    if (token.startsWith('</')) {
      if (stack.length === 1) return null;
      parent.end = start + token.length;
      parent.endTag = token;
      stack.pop();
    } else {
      const node = xmlLeafOrElement(token, start);
      parent.children.push(node);
      if (node.kind === 'element' && !token.endsWith('/>')) stack.push(node);
    }
  }
  return stack.length === 1 ? root.children : null;
}

function xmlLeafOrElement(token: string, start: number): XmlNode {
  const end = start + token.length;
  if (!token.startsWith('<')) return { kind: 'text', start, end, children: [] };
  const isTag = !token.startsWith('<!') && !token.startsWith('<?');
  return isTag
    ? { kind: 'element', start, end, startTag: token, children: [] }
    : { kind: 'other', start, end, children: [] };
}

function isWhitespaceText(node: XmlNode, content: string): boolean {
  return node.kind === 'text' && content.slice(node.start, node.end).trim() === '';
}

function emitXmlNode(node: XmlNode, content: string, depth: number): string {
  const indent = INDENT.repeat(depth);
  if (node.kind !== 'element' || !isElementOnly(node, content)) {
    return indent + content.slice(node.start, node.end);
  }
  const children = node.children
    .filter((child) => !isWhitespaceText(child, content))
    .map((child) => emitXmlNode(child, content, depth + 1));
  return [indent + node.startTag, ...children, indent + node.endTag].join('\n');
}

/**
 * An element whose content is only child elements (and comments / PIs) separated by whitespace —
 * the one case where that whitespace is layout, not data. Empty, text-only and mixed-content
 * elements, and anything that asks to keep its whitespace, are copied as written.
 */
function isElementOnly(node: XmlNode, content: string): boolean {
  if (!node.endTag || /\sxml:space\s*=\s*["']preserve["']/.test(node.startTag ?? '')) return false;
  const hasElementChild = node.children.some((child) => child.kind === 'element');
  const hasText = node.children.some((child) => child.kind === 'text' && !isWhitespaceText(child, content));
  return hasElementChild && !hasText && !hasCdataChild(node, content);
}

function hasCdataChild(node: XmlNode, content: string): boolean {
  return node.children.some(
    (child) => child.kind === 'other' && content.startsWith('<![CDATA[', child.start),
  );
}
